import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

interface Amenity {
  title?: string | null;
  group?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Photo slider
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const count = photos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || i}
          url={photo.image!}
          alt={photo.alt_text || photo.title || heading}
          className={`amenities-slider-img${i === current ? ' amenities-slider-img--active' : ''}`}
          sizes="(min-width: 900px) 50vw, 100vw"
          priority
        />
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Gallery cell
// ============================================================================

interface GalleryCellProps {
  photos: GalleryPhoto[];
  heading: string;
  cellClassName: string;
}

function GalleryCell({ photos, heading, cellClassName }: GalleryCellProps) {
  const ref = useScrollReveal<HTMLDivElement>();

  if (photos.length === 0) return null;

  return (
    <div ref={ref} className={`reveal-fade-up amenities-cell amenities-cell--gallery ${cellClassName}`}>
      <PhotoSlider photos={photos} heading={heading} />
    </div>
  );
}

// ============================================================================
// Amenity list cell (paginated text column)
// ============================================================================

const ITEMS_PER_PAGE = 8;

interface AmenityListCellProps {
  headingNode: ReactNode;
  items: Amenity[];
  cellClassName: string;
}

function AmenityListCell({ headingNode, items, cellClassName }: AmenityListCellProps) {
  const colRef = useScrollReveal<HTMLDivElement>();
  const [page, setPage] = useState(0);

  const pageCount = Math.max(1, Math.ceil(items.length / ITEMS_PER_PAGE));
  // Clamp in case the collection shrinks (e.g. live editing) below the current page.
  const currentPage = Math.min(page, pageCount - 1);
  const isPaginated = pageCount > 1;

  const visibleItems = useMemo(() => {
    if (!isPaginated) return items;
    const start = currentPage * ITEMS_PER_PAGE;
    return items.slice(start, start + ITEMS_PER_PAGE);
  }, [items, isPaginated, currentPage]);

  if (items.length === 0) return null;

  return (
    <div ref={colRef} className={`reveal-fade-up amenities-cell amenities-cell--list ${cellClassName}`}>
      {headingNode}
      <ul className="amenities-list-items">
        {visibleItems.map((item, i) => (
          <li key={currentPage * ITEMS_PER_PAGE + i} className="amenities-list-item">
            <span className="amenities-list-bullet" aria-hidden="true" />
            <span>{item.title || ''}</span>
          </li>
        ))}
      </ul>
      {isPaginated && (
        <nav className="amenities-list-pagination" aria-label="Amenities pagination">
          <button
            type="button"
            className="amenities-list-page-btn"
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage === 0}
            aria-label="Previous amenities"
          >
            <span aria-hidden="true">&#8592;</span>
          </button>
          <button
            type="button"
            className="amenities-list-page-btn"
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage === pageCount - 1}
            aria-label="Next amenities"
          >
            <span aria-hidden="true">&#8594;</span>
          </button>
        </nav>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function AmenitiesList() {
  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const interiorItems = useCollectionBinding<Amenity>('amenitieslist.interiorItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  useRevealStyles();

  const hasContent =
    communityPhotos.length > 0 ||
    apartmentPhotos.length > 0 ||
    communityItems.length > 0 ||
    interiorItems.length > 0;

  return (
    <section className="amenities-section">
      <div className="amenities-container">
        <RichTextContent
          path="amenitieslist.heading"
          label="Heading"
          defaultValue="<p>Amenities</p>"
          as="h2"
          className="amenities-heading"
        />
        <RichTextContent
          path="amenitieslist.intro"
          label="Intro"
          defaultValue="<p>Welcome to a world of unmatched luxury and convenience at The Aster. Our amenity offerings redefine the standard of modern living, providing an exquisite backdrop to complement your lifestyle.</p>"
          as="div"
          className="amenities-intro"
        />

        {hasContent && (
          // Desktop source order (cg, ag, cl, al) auto-places into two rows:
          // galleries on top, lists below. On mobile the cells are reordered
          // with `order` so each gallery sits directly above its matching list.
          <div className="amenities-grid">
            <GalleryCell
              photos={communityPhotos}
              heading="Community Amenities"
              cellClassName="amenities-cell--cg"
            />
            <GalleryCell
              photos={apartmentPhotos}
              heading="Interior Luxuries"
              cellClassName="amenities-cell--ag"
            />
            <AmenityListCell
              cellClassName="amenities-cell--cl"
              headingNode={
                <RichTextContent
                  path="amenitieslist.communityHeading"
                  label="Community Heading"
                  defaultValue="<p>Community Amenities</p>"
                  as="h3"
                  className="amenities-list-heading"
                />
              }
              items={communityItems}
            />
            <AmenityListCell
              cellClassName="amenities-cell--al"
              headingNode={
                <RichTextContent
                  path="amenitieslist.interiorHeading"
                  label="Interior Heading"
                  defaultValue="<p>Interior Amenities</p>"
                  as="h3"
                  className="amenities-list-heading"
                />
              }
              items={interiorItems}
            />
          </div>
        )}
      </div>

      <style>{`
        .amenities-section {
          position: relative;
          background: var(--vibe-color-primary);
          padding: 8rem 1.5rem 4.5rem;
        }
        /* Slanted top edge: white triangle overlays the top-left,
           leaving a diagonal that rises toward the right */
        .amenities-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 130px;
          background: #ffffff;
          clip-path: polygon(0 0, 100% 0, 0 100%);
        }

        .amenities-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .amenities-heading,
        .amenities-heading * {
          font-family: 'Oswald', 'Arial Narrow', system-ui, sans-serif;
          font-weight: 400;
        }
        .amenities-heading {
          font-size: 2.5rem;
          color: var(--vibe-color-secondary);
          text-align: center;
          margin: 0 0 1.5rem;
          letter-spacing: 0.05em;
        }

        .amenities-intro,
        .amenities-intro * {
          font-family: 'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
          font-weight: 400;
          font-style: normal;
        }
        /* Font size matches HeroSolidBG's blurb (Tailwind text-base md:text-lg):
           1rem below the md breakpoint (768px), 1.125rem at/above it. */
        .amenities-intro {
          font-size: 1rem;
          /* line-height + letter-spacing match HeroSolidBG's blurb
             (Tailwind leading-relaxed = 1.625, no tracking). */
          line-height: 1.625;
          color: #ffffff;
          text-align: center;
          max-width: 900px;
          margin: 0 auto 3rem;
          letter-spacing: normal;
        }
        @media (min-width: 768px) {
          .amenities-intro {
            font-size: 1.125rem;
          }
        }
        .amenities-intro p {
          margin: 0;
        }

        /* Two galleries (row 1) and two lists (row 2), side by side. */
        .amenities-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          column-gap: 2.5rem;
          row-gap: 3rem;
          align-items: start;
        }
        .amenities-cell {
          min-width: 0;
        }
        .amenities-cell--gallery {
          position: relative;
          aspect-ratio: 4 / 3;
          overflow: hidden;
          box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
        }
        .amenities-cell--list {
          display: flex;
          flex-direction: column;
          align-items: stretch;
        }

        .amenities-list-heading,
        .amenities-list-heading * {
          font-family: 'Oswald', 'Arial Narrow', system-ui, sans-serif;
          font-weight: 400;
        }
        .amenities-list-heading {
          font-size: clamp(1.75rem, 3vw, 2.5rem);
          color: var(--vibe-color-secondary);
          margin: 0 0 1.5rem;
          letter-spacing: 0.01em;
          text-align: center;
        }
        .amenities-list-items {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
        }
        .amenities-list-item {
          display: flex;
          align-items: baseline;
          gap: 0.85rem;
          font-family: ui-monospace, 'SF Mono', 'Menlo', 'Consolas', monospace;
          font-size: 1.05rem;
          line-height: 1.45;
          color: #ffffff;
        }
        .amenities-list-bullet {
          flex-shrink: 0;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: currentColor;
          transform: translateY(-0.35em);
        }
        .amenities-list-pagination {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 0.5rem;
          margin-top: 1.5rem;
          margin-left: -0.5rem;
        }
        .amenities-list-page-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          line-height: 1;
          color: #ffffff;
          background: transparent;
          border: none;
          padding: 0.25rem 0.5rem;
          cursor: pointer;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .amenities-list-page-btn:hover:not(:disabled) {
          transform: scale(1.15);
        }
        .amenities-list-page-btn:disabled {
          opacity: 0.35;
          cursor: default;
        }

        @media (max-width: 900px) {
          .amenities-heading {
            font-size: 2rem;
          }
          .amenities-grid {
            grid-template-columns: 1fr;
            row-gap: 1.5rem;
          }
          /* Single column: interleave so each gallery sits directly above
             its matching list (community gallery, community list,
             apartment gallery, apartment list). */
          .amenities-cell--cg { order: 1; }
          .amenities-cell--cl { order: 2; }
          .amenities-cell--ag { order: 3; }
          .amenities-cell--al { order: 4; }
          /* Tighten the gap between a gallery and its own list. */
          .amenities-cell--cl,
          .amenities-cell--al {
            margin-top: -0.5rem;
          }
        }

        @media (max-width: 600px) {
          .amenities-section {
            padding: 5rem 1rem 3.5rem;
          }
          .amenities-section::before {
            height: 60px;
          }
          .amenities-heading {
            font-size: 1.6rem;
          }
          .amenities-list-item {
            font-size: 0.95rem;
          }
        }

        /* Photo slider */
        .amenities-slider {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }
        .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slider-img--active {
          opacity: 1;
        }

        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 40px;
          height: 40px;
          border: none;
          background: transparent;
          color: rgba(255, 255, 255, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: color 0.25s, transform 0.25s;
          z-index: 2;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4));
        }
        .amenities-slider-arrow:hover {
          color: #ffffff;
        }
        .amenities-slider-arrow--prev { left: 6px; }
        .amenities-slider-arrow--next { right: 6px; }
        .amenities-slider-arrow--prev:hover { transform: translateY(-50%) translateX(-2px); }
        .amenities-slider-arrow--next:hover { transform: translateY(-50%) translateX(2px); }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slider-img {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Amenities</p>',
    },
    'amenitieslist.intro': {
      label: 'Intro',
      type: 'richtext',
      defaultField: 'property_amenities_blurb',
      defaultValue:
        '<p>Welcome to a world of unmatched luxury and convenience at The Aster. Our amenity offerings redefine the standard of modern living, providing an exquisite backdrop to complement your lifestyle.</p>',
    },
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Community Amenities</p>',
    },
    'amenitieslist.interiorHeading': {
      label: 'Interior Heading',
      type: 'richtext',
      defaultValue: '<p>Interior Amenities</p>',
    },
  },
  collectionBindings: {
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.interiorItems': {
      label: 'Interior Amenities',
      collection: 'amenities',
    },
  },
};

(AmenitiesList as any).contentSchema = contentSchema;

export default AmenitiesList;
